import { useState, useCallback } from "react"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core"
import { arrayMove } from "@dnd-kit/sortable"
import { toaster } from "components/common/Toast"
import {
  addAIDashboardWidget,
  deleteAIDashboardWidget,
  reorderAIDashboardWidgets,
  resetAIDashboardWidgets
} from "store/aiDashboard"
import { buildPeriodParams } from "../utils"

export default function useDashboardWidgets({ activeDateFilter, dateRange, singleDate, fetchDashboardData, fetchAllKpis }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const [isEditing, setIsEditing] = useState(false)
  const [isAddWidgetOpen, setAddWidgetOpen] = useState(false)
  const [isAddWidgetLoading, setAddWidgetLoading] = useState(false)
  const [isResetLoading, setResetLoading] = useState(false)

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }))

  const refresh = useCallback(() => {
    fetchDashboardData(activeDateFilter, dateRange, singleDate)
  }, [fetchDashboardData, activeDateFilter, dateRange, singleDate])

  const handleAddWidget = ({ insightType, chartType }) => {
    const params = buildPeriodParams(activeDateFilter, dateRange, singleDate)
    const payload = {
      insight_type: insightType,
      chart_type: chartType,
      time_period: params.period ?? "last_30_days",
      ...(params.date_range ? { date_range: params.date_range } : {})
    }
    setAddWidgetLoading(true)
    dispatch(addAIDashboardWidget(payload))
      .then(() => {
        refresh()
        setAddWidgetOpen(false)
      })
      .catch((err) => {
        const errorMsg = err?.data?.error || err?.response?.data?.error || t("ai_dashboard.failed_add_widget")
        toaster.error({ title: errorMsg })
      })
      .finally(() => setAddWidgetLoading(false))
  }

  const handleDeleteWidget = (apiId, setWidgets) => {
    dispatch(deleteAIDashboardWidget(apiId))
      .then(() => {
        toaster.success({ title: t("ai_dashboard.delete_widget_success") })
        refresh()
      })
      .catch(() => {
        toaster.error({ title: t("ai_dashboard.delete_widget_error") })
        refresh()
      })
  }

  const handleDragEnd = (event, setWidgets) => {
    const { active, over } = event
    if (active.id !== over?.id) {
      setWidgets((items) => {
        const oldIndex = items.findIndex((w) => w.id === active.id)
        const newIndex = items.findIndex((w) => w.id === over.id)
        const reordered = arrayMove(items, oldIndex, newIndex)
        const positions = reordered.filter((w) => w.apiId).map((w, i) => ({ widget_id: w.apiId, position: i }))
        if (positions.length > 0) dispatch(reorderAIDashboardWidgets(positions)).catch(() => {})
        return reordered
      })
    }
  }

  const handleReset = () => {
    setResetLoading(true)
    dispatch(resetAIDashboardWidgets())
      .then(() => {
        toaster.success({ title: t("ai_dashboard.reset_widgets_success") })
        fetchDashboardData(activeDateFilter, dateRange, singleDate)
        fetchAllKpis(activeDateFilter, dateRange, singleDate)
      })
      .catch(() => toaster.error({ title: t("ai_dashboard.reset_widgets_error") }))
      .finally(() => setResetLoading(false))
  }

  return {
    isEditing, setIsEditing,
    isAddWidgetOpen, setAddWidgetOpen,
    isAddWidgetLoading,
    isResetLoading,
    sensors,
    handleAddWidget,
    handleDeleteWidget,
    handleDragEnd,
    handleReset
  }
}
